import {isYearlyWeatherLayerName} from '../../utils/weather-layer-name.util';

const DEVIATION_PRECIPITATION = 'PrecipitationDeviation';
const DEVIATION_TEMPERATURE = 'TemperatureDeviation';

/** More specific keys must come before shorter substrings (SumOfFallout before Fallout). */
export const WEATHER_LEGEND_CONFIG_KEYS = [
  'SumOfFallout',
  'SumOfSolarRadiation',
  'SumOfActiveTemperaturesBase5',
  'SumOfActiveTemperaturesBase7',
  'SumOfActiveTemperaturesBase10',
  'SumOfActiveTemperaturesBase12',
  'SumOfActiveTemperaturesBase15',
  'PrecipitationTotal',
  'Humidity',
  'Fallout',
  'TemperatureAverageTotal',
  'TemperatureMaxTotal',
  'TemperatureMinTotal',
  'TemperatureAverage',
  'TemperatureMax',
  'TemperatureMin',
  'FrostDanger',
  'WindSpeed',
  'PrecipitationDeviation',
  'TemperatureDeviation',
  'SolarRadiationInfluence',
  'WindDirection'
] as const;

export const WEATHER_LEGEND_TITLE_KEYS: Record<string, string> = {
  SumOfFallout: 'SUM_OF_FALLOUT_MM',
  SumOfSolarRadiation: 'SUM_OF_SOLAR_RADIATION_MJ',
  SumOfActiveTemperaturesBase5: 'SUM_OF_ACTIVE_TEMPERATURES_BASE5_C',
  SumOfActiveTemperaturesBase7: 'SUM_OF_ACTIVE_TEMPERATURES_BASE7_C',
  SumOfActiveTemperaturesBase10: 'SUM_OF_ACTIVE_TEMPERATURES_BASE10_C',
  SumOfActiveTemperaturesBase12: 'SUM_OF_ACTIVE_TEMPERATURES_BASE12_C',
  SumOfActiveTemperaturesBase15: 'SUM_OF_ACTIVE_TEMPERATURES_BASE15_C',
  PrecipitationTotal: 'PRECIPITATION_MM',
  TemperatureAverageTotal: 'TEMPERATURE_AVERAGE_TOTAL_C',
  TemperatureAverage: 'TEMPERATURE_C',
  TemperatureMax: 'TEMPERATURE_MAX_C',
  TemperatureMin: 'TEMPERATURE_MIN_C',
  Humidity: 'HUMIDITY_PERCENT',
  Fallout: 'PRECIPITATION_MM',
  WindSpeed: 'WIND_SPEED_MS',
  WindDirection: 'WIND_DIRECTION_DEG',
  PrecipitationDeviation: 'PRECIPITATION_DEVIATION',
  TemperatureDeviation: 'TEMPERATURE_DEVIATION_C',
  HumidityDeviation: 'HUMIDITY_PERCENT',
  GenericDeviation: 'Deviation',
  SolarRadiationInfluence: 'SOLAR_RADIATION_JCM2',
  FrostDanger: 'FrostDanger'
};

/** Yearly ATD layers keep the same legend scale but use titles that end with the year phrase. */
export const YEARLY_WEATHER_LEGEND_TITLE_KEYS: Record<string, string> = {
  SumOfFallout: 'YEARLY_SUM_OF_FALLOUT_MM',
  TemperatureAverageTotal: 'YEARLY_TEMPERATURE_AVERAGE_TOTAL_C',
  PrecipitationDeviation: 'YEARLY_PRECIPITATION_DEVIATION',
  TemperatureDeviation: 'YEARLY_TEMPERATURE_DEVIATION_C'
};

const GRADIENT_LEGEND_KEYS = new Set([
  'Humidity',
  'PrecipitationTotal',
  'Fallout',
  'SolarRadiationInfluence',
  'TemperatureAverage',
  'TemperatureMax',
  'TemperatureMin',
  'TemperatureMaxTotal',
  'TemperatureMinTotal',
  'TemperatureAverageTotal',
  'SumOfActiveTemperaturesBase5',
  'SumOfActiveTemperaturesBase7',
  'SumOfActiveTemperaturesBase10',
  'SumOfActiveTemperaturesBase12',
  'SumOfActiveTemperaturesBase15',
  'SumOfFallout',
  'SumOfSolarRadiation'
]);

/**
 * Resolve the legend config key for a GeoServer layer name.
 * Deviation MUST win over accumulated keys (EraDeviationSumOfFallout2026).
 */
export function matchWeatherLegendKey(layer: string | null | undefined): string | null {
  if (!layer) {
    return null;
  }

  if (/Deviation/i.test(layer)) {
    if (isYearlyWeatherLayerName(layer)) {
      if (/SumOfFallout|Fallout/i.test(layer)) {
        return 'Fallout';
      }
      if (/Temperature/i.test(layer)) {
        return 'TemperatureAverage';
      }
    } else {
      if (/SumOfFallout|Fallout|Precipitation/i.test(layer)) {
        return DEVIATION_PRECIPITATION;
      }
      if (/Temperature/i.test(layer)) {
        return DEVIATION_TEMPERATURE;
      }
      if (/Humidity/i.test(layer)) {
        return 'HumidityDeviation';
      }
      return 'GenericDeviation';
    }
  }

  for (const key of WEATHER_LEGEND_CONFIG_KEYS) {
    if (layer.includes(key)) {
      return key;
    }
  }

  return null;
}

export function weatherLegendTitleKey(layer: string | null | undefined): string | null {
  const key = matchWeatherLegendKey(layer);
  if (!key || !layer) {
    return null;
  }
  if (isYearlyWeatherLayerName(layer) && /Deviation/i.test(layer)) {
    if (/SumOfFallout|Fallout/i.test(layer)) {
      return YEARLY_WEATHER_LEGEND_TITLE_KEYS[DEVIATION_PRECIPITATION] ?? null;
    }
    if (/TemperatureAverageTotal/i.test(layer)) {
      return YEARLY_WEATHER_LEGEND_TITLE_KEYS[DEVIATION_TEMPERATURE] ?? null;
    }
  }
  if (isYearlyWeatherLayerName(layer)) {
    return YEARLY_WEATHER_LEGEND_TITLE_KEYS[key] ?? WEATHER_LEGEND_TITLE_KEYS[key] ?? null;
  }
  return WEATHER_LEGEND_TITLE_KEYS[key] ?? null;
}

/** YEARLY_* i18n key for GIS dropdown copy, or null when the layer is not a yearly ATD tile. */
export function yearlyAtdDropdownTitleKey(layer: string | null | undefined): string | null {
  if (!isYearlyWeatherLayerName(layer)) {
    return null;
  }
  const key = matchWeatherLegendKey(layer);
  return key ? (YEARLY_WEATHER_LEGEND_TITLE_KEYS[key] ?? null) : null;
}

/** Dropdown labels use the title line only; legend keys keep the unit on the next line. */
export function yearlyAtdDropdownLabel(translated: string): string {
  return translated.split('\n')[0].trim();
}

export function isWeatherLegendGradient(layer: string | null | undefined): boolean {
  const key = matchWeatherLegendKey(layer);
  return !!key && GRADIENT_LEGEND_KEYS.has(key);
}

/** Daily ERA deviation tiles use three fixed semantic bands (docs/WEATHER.md). */
export function isDailyDeviationBandLegend(layer: string | null | undefined): boolean {
  if (!layer || !/Deviation/i.test(layer) || isYearlyWeatherLayerName(layer)) {
    return false;
  }
  const key = matchWeatherLegendKey(layer);
  return key === 'PrecipitationDeviation'
    || key === 'TemperatureDeviation'
    || key === 'HumidityDeviation';
}

export function weatherLegendCssBaseName(layer: string | null | undefined): string {
  const key = matchWeatherLegendKey(layer);
  if (key) {
    return key.toLowerCase();
  }
  return (layer ?? '').toLowerCase();
}
